import { useState, useEffect } from "react";
import { computeSpread } from "../lib/spread";
import { pairLegs, pairId } from "../lib/pairs";

export { pairId };

// 여러 레그를 기하평균으로 묶어 바스켓 가격 시계열 생성 (단일레그면 그 자체)
function buildBasket(legs, series, minLen = 30) {
  if (!legs || !legs.length) return null;
  const maps = legs.map((l) => {
    const pts = series?.[l.ticker]?.points;
    if (!pts) return null;
    const m = new Map();
    for (const [ts, c] of pts) if (c > 0) m.set(ts, c);
    return m.size ? m : null;
  });
  if (maps.some((m) => !m)) return null;
  let common = [...maps[0].keys()];
  for (let i = 1; i < maps.length; i++) common = common.filter((ts) => maps[i].has(ts));
  common.sort((a, b) => a - b);
  if (common.length < minLen) return null;
  return common.map((ts) => {
    let s = 0;
    for (const m of maps) s += Math.log(m.get(ts));
    return [ts, Math.exp(s / maps.length)];
  });
}

// pairs: [{longs:[{ticker,market}], shorts:[...]}] (또는 구모델), range: "1y"|"3y"|"5y"
export function usePairData(pairs, range, interval = "1d") {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!pairs || !pairs.length) { setData({}); return; }
    let cancelled = false;
    setLoading(true);

    const symMap = new Map();   // ticker -> "ticker:market"
    pairs.forEach((p) => {
      const { longs, shorts } = pairLegs(p);
      [...longs, ...shorts].forEach((l) => { if (l.ticker) symMap.set(l.ticker, `${l.ticker}:${l.market || "KR"}`); });
    });
    const symbols = [...symMap.values()].join(",");

    fetch(`/api/history?symbols=${encodeURIComponent(symbols)}&range=${range}&interval=${interval}`)
      .then((r) => r.json())
      .then(({ series }) => {
        if (cancelled) return;
        const minLen = interval === "1wk" ? 20 : 30;   // 주봉 6개월(~26주)도 통과하도록 완화
        const out = {};
        pairs.forEach((p) => {
          const { longs, shorts } = pairLegs(p);
          const lb = buildBasket(longs, series, minLen);
          const sb = buildBasket(shorts, series, minLen);
          out[pairId(p)] = (lb && sb) ? computeSpread(lb, sb, minLen) : null;
        });
        setData(out);
      })
      .catch(() => { if (!cancelled) setData({}); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [pairs, range, interval]);

  return { data, loading };
}
